import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupsIcon from '@mui/icons-material/Groups';
import MasteryRing from '../fx/MasteryRing';
import { masteryTone, orchard } from '../../theme';

// «Сегодня» / «Вчера» / «5 дн. назад» / «Никогда»
export function lastAssessedLabel(days, en) {
  if (days === null || days === undefined) return en ? 'Never' : 'Никогда';
  if (days === 0) return en ? 'Today' : 'Сегодня';
  if (days === 1) return en ? 'Yesterday' : 'Вчера';
  return en ? `${days}d ago` : `${days} дн. назад`;
}

function freshnessColor(days, dark) {
  if (days === null || days === undefined || days >= 30)
    return dark ? orchard.cherryBright : orchard.cherry;
  if (days >= 14) return orchard.honey;
  return dark ? orchard.leafBright : orchard.leaf;
}

/* One student, one glance: mastery ring, name, group, when they were last
   assessed and how often they attend. The whole card opens the profile;
   «Оценить» continues the latest assessment. Carries data-reveal/data-press
   so the page's stagger and press feedback pick it up. */
export default function StudentCard({
  student,
  groupNames = [],
  onNavigate,
  onQuickAssess,
  onTagClick,
  onMerge,
  activeTagFilter,
  en,
  index = 0,
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [menuEl, setMenuEl] = useState(null);

  const assessed = student.profileCount > 0;
  const tone = masteryTone(student.avgMastery, assessed, dark);
  const days = student.daysSinceAssessed;
  const overdue = days === null || days === undefined || days >= 30;
  const fresh = freshnessColor(days, dark);
  const attendance =
    student.attendanceRate !== null && student.attendanceRate !== undefined
      ? student.attendanceRate
      : null;
  const trend =
    student.masteryTrend === 'down'
      ? { sym: '↓', color: dark ? orchard.cherryBright : orchard.cherry }
      : student.masteryTrend === 'up'
        ? { sym: '↑', color: dark ? orchard.leafBright : orchard.leaf }
        : null;

  const groupLine = [
    groupNames.length > 2
      ? `${groupNames.slice(0, 2).join(', ')} +${groupNames.length - 2}`
      : groupNames.join(', '),
    student.templateName,
  ]
    .filter(Boolean)
    .join(' · ');

  const open = () => onNavigate(student._id);
  const masteredText = assessed
    ? en
      ? `${student.totalMastered} mastered of ${student.totalAssessed}`
      : `Усвоено ${student.totalMastered} из ${student.totalAssessed}`
    : en
      ? 'Not assessed yet'
      : 'Ещё не оценён';

  return (
    <Box
      data-reveal
      data-press
      role="link"
      tabIndex={0}
      aria-label={student.name}
      onClick={open}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        pl: 2,
        borderRadius: '18px',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: overdue
          ? dark
            ? 'rgba(242,89,114,0.35)'
            : 'rgba(194,35,62,0.22)'
          : 'divider',
        cursor: 'pointer',
        overflow: 'hidden',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        transition: `border-color 0.25s ${orchard.ease}, box-shadow 0.25s ${orchard.ease}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 14,
          bottom: 14,
          width: 4,
          borderRadius: '0 4px 4px 0',
          bgcolor: tone,
        },
        '@media (hover: hover)': {
          '&:hover': {
            borderColor: tone,
            boxShadow: dark
              ? '0 14px 30px -16px rgba(0,0,0,0.7)'
              : '0 14px 30px -16px rgba(36,30,43,0.28)',
          },
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Tooltip title={masteredText} enterTouchDelay={400}>
          <Box
            component="span"
            sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
          >
            {/* With a photo the ring goes around the child's face and the
                mastery % moves into a badge under it. */}
            <MasteryRing
              value={assessed ? student.avgMastery : null}
              color={tone}
              size={student.photo ? 58 : 52}
              stroke={student.photo ? 4 : 5}
              delay={Math.min(index, 10) * 0.04}
              trackColor={dark ? '#F4EEF6' : orchard.ink}
              label={`${student.name}: ${masteredText}`}
            >
              {student.photo ? (
                <Box
                  component="img"
                  src={student.photo}
                  alt=""
                  loading="lazy"
                  sx={{
                    position: 'relative',
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    display: 'block',
                    bgcolor: 'action.hover',
                  }}
                />
              ) : undefined}
            </MasteryRing>
            {student.photo && assessed && (
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: -7,
                  transform: 'translateX(-50%)',
                  px: 0.6,
                  minWidth: 30,
                  textAlign: 'center',
                  borderRadius: 999,
                  bgcolor: tone,
                  color: '#fff',
                  fontSize: '0.64rem',
                  fontWeight: 800,
                  lineHeight: '15px',
                  fontVariantNumeric: 'tabular-nums',
                  border: '2px solid',
                  borderColor: 'background.paper',
                }}
              >
                {Math.round(student.avgMastery)}%
              </Box>
            )}
          </Box>
        </Tooltip>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              noWrap
              sx={{ fontWeight: 800, fontSize: '0.98rem', lineHeight: 1.25 }}
            >
              {student.name}
            </Typography>
            {trend && (
              <Typography
                component="span"
                sx={{ fontWeight: 800, fontSize: '0.8rem', color: trend.color }}
              >
                {trend.sym}
              </Typography>
            )}
          </Box>
          <Typography
            noWrap
            color="text.secondary"
            sx={{
              fontSize: '0.74rem',
              lineHeight: 1.35,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minHeight: 16,
            }}
          >
            {groupLine ? (
              <>
                <GroupsIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {groupLine}
                </span>
              </>
            ) : (
              ' '
            )}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.25,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                minWidth: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: fresh,
                  flexShrink: 0,
                }}
              />
              <Typography
                noWrap
                component="span"
                sx={{ fontSize: '0.76rem', fontWeight: 700, color: fresh }}
              >
                {lastAssessedLabel(days, en)}
              </Typography>
            </Box>
            {attendance !== null && (
              <Typography
                noWrap
                component="span"
                color={attendance < 70 ? 'warning.main' : 'text.secondary'}
                sx={{
                  fontSize: '0.74rem',
                  fontWeight: attendance < 70 ? 800 : 500,
                }}
              >
                {en ? 'att.' : 'посещ.'} {attendance}%
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton
          size="small"
          aria-label={en ? 'More actions' : 'Ещё действия'}
          onClick={(e) => {
            e.stopPropagation();
            setMenuEl(e.currentTarget);
          }}
          sx={{ alignSelf: 'flex-start', mr: -0.5, mt: -0.5 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={() => setMenuEl(null)}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            onClick={() => {
              setMenuEl(null);
              if (onMerge) onMerge(student);
            }}
          >
            <ListItemIcon>
              <CallMergeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{en ? 'Merge into…' : 'Объединить с…'}</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {(student.tags || []).length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {student.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onTagClick) onTagClick(tag);
              }}
              color={activeTagFilter === tag ? 'primary' : 'default'}
              sx={{
                fontSize: '0.64rem',
                height: 20,
                flexShrink: 0,
                '& .MuiChip-label': { px: 0.9 },
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
        {student.latestProfileId && (
          <Tooltip
            title={
              en
                ? 'Continue the latest assessment (changes are saved into it)'
                : 'Продолжить последнюю оценку (изменения сохраняются в неё)'
            }
            enterTouchDelay={600}
          >
            <Button
              variant="contained"
              disableElevation
              startIcon={<BoltIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onQuickAssess(student);
              }}
              sx={{
                flex: 1,
                minHeight: 40,
                py: 0.5,
                px: 1.5,
                fontSize: '0.82rem',
              }}
            >
              {en ? 'Assess' : 'Оценить'}
            </Button>
          </Tooltip>
        )}
        <Button
          variant={student.latestProfileId ? 'outlined' : 'contained'}
          disableElevation
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
          sx={{
            flex: 1,
            minHeight: 40,
            py: 0.5,
            px: 1.5,
            fontSize: '0.82rem',
          }}
        >
          {student.latestProfileId
            ? en
              ? 'Profile'
              : 'Профиль'
            : en
              ? 'Start first assessment'
              : 'Начать первую оценку'}
        </Button>
      </Box>
    </Box>
  );
}
